import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { platformTenantsApi, platformTenantDomainsApi } from '@/api/platform.api';
import { qk } from '@/config/queryKeys';
import type { GetTenantsParams } from '@/types/platform';

// Platform tenant registry (control-plane). Requires a platform session token.

export function usePlatformTenants(params: GetTenantsParams) {
  return useQuery({
    queryKey: qk.platformTenants.list(params),
    queryFn: () => platformTenantsApi.list(params),
    placeholderData: keepPreviousData,
  });
}

export function usePlatformTenant(id: number) {
  return useQuery({
    queryKey: qk.platformTenants.detail(id),
    queryFn: () => platformTenantsApi.get(id),
    enabled: id > 0,
  });
}

/** O13 tenant page: business profile from onboarding, trial and suggested plan. */
export function usePlatformTenantProfile(id: number) {
  return useQuery({
    queryKey: qk.platformTenants.profile(id),
    queryFn: () => platformTenantsApi.profile(id),
    enabled: id > 0,
  });
}

/** Extends the trial (super admin). The response is the refreshed page, written straight into the cache. */
export function useExtendTrial(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { days: number; reason: string }) => platformTenantsApi.extendTrial(id, vars.days, vars.reason),
    onSuccess: (data) => {
      qc.setQueryData(qk.platformTenants.profile(id), data);
      qc.invalidateQueries({ queryKey: qk.platformTenants.all });
    },
  });
}

// ---- custom domains ----

const DOMAINS = 'platform-tenant-domains';

export function useTenantDomains(tenantId: number | undefined) {
  return useQuery({
    queryKey: [DOMAINS, tenantId ?? 'none'],
    queryFn: () => platformTenantDomainsApi.list(tenantId as number),
    enabled: !!tenantId,
  });
}

function useDomainInvalidator(tenantId: number) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [DOMAINS, tenantId] });
}

export function useSetBaseDomain(tenantId: number) {
  const invalidate = useDomainInvalidator(tenantId);
  return useMutation({
    mutationFn: (baseDomain: string | null) => platformTenantDomainsApi.setBase(tenantId, baseDomain),
    onSuccess: invalidate,
  });
}

export function useAddTenantDomain(tenantId: number) {
  const invalidate = useDomainInvalidator(tenantId);
  return useMutation({
    mutationFn: (domain: string) => platformTenantDomainsApi.add(tenantId, domain),
    onSuccess: invalidate,
  });
}

export function useSetDomainVerified(tenantId: number) {
  const invalidate = useDomainInvalidator(tenantId);
  return useMutation({
    mutationFn: (vars: { domainId: number; isVerified: boolean }) =>
      platformTenantDomainsApi.setVerified(tenantId, vars.domainId, vars.isVerified),
    onSuccess: invalidate,
  });
}

export function useSetPrimaryDomain(tenantId: number) {
  const invalidate = useDomainInvalidator(tenantId);
  return useMutation({
    mutationFn: (domainId: number) => platformTenantDomainsApi.setPrimary(tenantId, domainId),
    onSuccess: invalidate,
  });
}

export function useRemoveTenantDomain(tenantId: number) {
  const invalidate = useDomainInvalidator(tenantId);
  return useMutation({
    mutationFn: (domainId: number) => platformTenantDomainsApi.remove(tenantId, domainId),
    onSuccess: invalidate,
  });
}

export function useSuspendTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => platformTenantsApi.suspend(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.platformTenants.all }),
  });
}

export function useActivateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => platformTenantsApi.activate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.platformTenants.all }),
  });
}
